import { Component, LLMProvider, T_RESPONSE, CollectionRegistry, Tool, DashCompModelConfig } from "../types";
import { logger } from "../utils/logger";
import { LLM } from "../llm";
import { promptLoader } from "../userResponse/prompt-loader";
import { schema } from "../userResponse/schema";
import { userPromptErrorLogger } from "../utils/user-prompt-error-logger";
import { getApiKeyAndModel, formatComponentsForPrompt, formatToolsForPrompt } from "./utils";
import { getCurrentDateTimeForPrompt } from "../utils/datetime";
import { ensureQueryLimit, validateAndFixSqlQuery, getDbTypeFromToolId } from "../userResponse/utils";
import { MAX_COMPONENT_QUERY_LIMIT, DEFAULT_QUERY_LIMIT, MAX_QUERY_VALIDATION_RETRIES, KNOWLEDGE_BASE_TOP_K } from "../userResponse/constants";
import { queryCache } from "../utils/query-cache";
import KB from "../userResponse/knowledge-base";
import { extractPromptText } from "../userResponse/prompt-extractor";

/**
 * Generate multiple report components using LLM with tool support.
 *
 * Similar to pickComponentWithLLM but:
 * - Uses report-comp-picker prompt (generates MULTIPLE components)
 * - Returns an array of components with layout metadata
 * - Validates all externalTool SQL queries
 */
export async function generateReportComponents(
	prompt: string,
	components: Component[],
	anthropicApiKey?: string,
	groqApiKey?: string,
	geminiApiKey?: string,
	openaiApiKey?: string,
	llmProviders?: LLMProvider[],
	collections?: CollectionRegistry,
	tools?: Tool[],
	modelConfig?: DashCompModelConfig,
	conversationHistory?: string,
	userId?: string
): Promise<T_RESPONSE> {
	const errors: string[] = [];

	const availableComponentsText = formatComponentsForPrompt(components);
	const availableToolsText = formatToolsForPrompt(tools);

	try {
		// Check if any tool is a database source tool
		const hasDatabaseSourceTool = tools?.some(tool => {
			const params = tool.params || {};
			return 'sql' in params || Object.keys(params).some(key => key.toLowerCase() === 'sql');
		}) ?? false;

		let schemaDoc: string;
		if (hasDatabaseSourceTool) {
			schemaDoc = 'Schema is available in the database source tool descriptions.';
		} else {
			schemaDoc = schema.generateSchemaDocumentation();
		}

		const databaseRules = await promptLoader.loadDatabaseRules();

		// Knowledge base context
		let globalKnowledgeBase = 'No global knowledge base available.';
		let knowledgeBaseContext = 'No additional knowledge base context available.';
		if (collections) {
			const kbResult = await KB.getAllKnowledgeBase({
				prompt,
				collections,
				userId,
				topK: KNOWLEDGE_BASE_TOP_K,
			});
			globalKnowledgeBase = kbResult.globalContext || globalKnowledgeBase;
			const dynamicParts: string[] = [];
			if (kbResult.userContext) {
				dynamicParts.push('## User-Specific Knowledge Base\n' + kbResult.userContext);
			}
			if (kbResult.queryContext) {
				dynamicParts.push('## Relevant Knowledge Base (Query-Matched)\n' + kbResult.queryContext);
			}
			knowledgeBaseContext = dynamicParts.join('\n\n') || knowledgeBaseContext;
		}

		// Load report-specific prompts
		const prompts = await promptLoader.loadPrompts('report-comp-picker', {
			USER_PROMPT: prompt,
			AVAILABLE_COMPONENTS: availableComponentsText,
			SCHEMA_DOC: schemaDoc || 'No database schema available',
			DATABASE_RULES: databaseRules,
			AVAILABLE_TOOLS: availableToolsText,
			CURRENT_DATETIME: getCurrentDateTimeForPrompt(),
			CONVERSATION_HISTORY: conversationHistory || 'No previous conversation',
			GLOBAL_KNOWLEDGE_BASE: globalKnowledgeBase,
			KNOWLEDGE_BASE_CONTEXT: knowledgeBaseContext,
		});

		logger.logLLMPrompt('reportCompPicker', 'system', extractPromptText(prompts.system));
		logger.logLLMPrompt('reportCompPicker', 'user', prompts.user);
		logger.debug('[REPORT_COMP_REQ] Loaded report-comp-picker prompts with schema and tools');

		const { apiKey, model } = getApiKeyAndModel(
			anthropicApiKey, groqApiKey, geminiApiKey, openaiApiKey, llmProviders, modelConfig
		);

		if (!apiKey || !model) {
			errors.push('No API key available for any LLM provider');
			return { success: false, errors };
		}

		logger.info(`[REPORT_COMP_REQ] Using model: ${model}`);

		// Track executed tools
		const executedTools: Array<{ id: string; name: string; params: any; result: any; outputSchema?: any }> = [];

		// Build LLM tool definitions from external tools
		const llmTools = (tools || []).map(tool => {
			const properties: Record<string, any> = {};
			const required: string[] = [];

			Object.entries(tool.params || {}).forEach(([key, typeOrValue]) => {
				const valueStr = String(typeOrValue).toLowerCase();
				let schemaType = 'string';
				const typeMatch = valueStr.match(/^(string|number|integer|boolean|array|object)\b/);
				if (typeMatch) schemaType = typeMatch[1];

				const isOptional = valueStr.includes('(optional)') || valueStr.includes('optional');
				const description = typeof typeOrValue === 'string' ? typeOrValue : `Parameter: ${key}`;

				if (schemaType === 'array') {
					properties[key] = { type: 'array', items: { type: 'string' }, description };
				} else if (schemaType === 'object') {
					properties[key] = { type: 'object', description };
				} else {
					properties[key] = { type: schemaType, description };
				}

				if (!isOptional) required.push(key);
			});

			return {
				name: tool.id,
				description: tool.description,
				input_schema: {
					type: 'object',
					properties,
					required: required.length > 0 ? required : undefined,
				},
			};
		});

		// Tool handler
		const toolHandler = async (toolName: string, toolInput: any): Promise<string> => {
			const tool = tools?.find(t => t.id === toolName);
			if (!tool) throw new Error(`Unknown tool: ${toolName}`);

			logger.info(`[REPORT_COMP_REQ] Executing external tool: ${tool.name} (${tool.id})`);
			const result = await tool.fn(toolInput);

			executedTools.push({
				id: tool.id,
				name: tool.name,
				params: toolInput,
				result,
				outputSchema: tool.outputSchema
			});

			logger.info(`[REPORT_COMP_REQ] Tool ${tool.name} executed successfully`);
			const resultJson = JSON.stringify(result, null, 2);
			return resultJson + '\n\n[REMINDER: The above is tool output for verification. You MUST still respond with ONLY the JSON report object containing multiple components. Do NOT summarize these results.]';
		};

		// LLM call with tool support
		const result = await LLM.streamWithTools(
			{
				sys: prompts.system,
				user: prompts.user
			},
			llmTools,
			toolHandler,
			{
				model,
				maxTokens: 16384,
				temperature: 0.2,
				apiKey
			},
			5
		);

		// Parse JSON response
		let jsonMatch = result.match(/\{[\s\S]*\}/);
		let parsedResult = jsonMatch ? (() => { try { return JSON.parse(jsonMatch![0]); } catch { return null; } })() : null;

		// Retry if first attempt failed with tool data baked in
		if (!parsedResult?.components && executedTools.length > 0) {
			const toolDataSummary = executedTools.map(t =>
				`Tool "${t.name}" was called with params ${JSON.stringify(t.params)} and returned:\n${JSON.stringify(t.result, null, 2).substring(0, 5000)}`
			).join('\n\n');

			const retryUserPrompt = `Original user request: ${prompt}\n\nThe following tools were already called and returned data:\n${toolDataSummary}\n\nUsing this data, generate the complete report with multiple components. Respond with ONLY the JSON report object. No explanation, no markdown, just JSON.`;

			const retryResult = await LLM.text(
				{
					sys: prompts.system,
					user: retryUserPrompt
				},
				{
					model,
					maxTokens: 16384,
					temperature: 0.1,
					apiKey
				}
			);

			jsonMatch = retryResult.match(/\{[\s\S]*\}/);
			parsedResult = jsonMatch ? (() => { try { return JSON.parse(jsonMatch![0]); } catch { return null; } })() : null;
		}

		if (!parsedResult || !parsedResult.components) {
			errors.push('Failed to parse LLM response as report JSON');
			errors.push(`LLM Response: ${result}`);
			logger.error(`[REPORT_COMP_REQ] Failed to parse report JSON from LLM response`);
			return { success: false, errors };
		}

		logger.info(`[REPORT_COMP_REQ] LLM generated ${parsedResult.components.length} report components`);

		// Build final component objects
		const finalComponents: Component[] = [];
		for (const compData of parsedResult.components) {
			if (!compData.componentId || !compData.props) {
				logger.warn(`[REPORT_COMP_REQ] Skipping component with missing componentId or props`);
				continue;
			}

			const originalComponent = components.find(c => c.name === compData.componentName);
			if (!originalComponent) {
				logger.warn(`[REPORT_COMP_REQ] Component ${compData.componentName} not found, skipping`);
				continue;
			}

			let finalComp: Component = {
				...originalComponent,
				props: {
					...originalComponent.props,
					...compData.props
				}
			};

			// Ensure query limits
			const etSql = (finalComp.props as any)?.externalTool?.parameters?.sql;
			if (etSql && typeof etSql === 'string') {
				const etToolId = (finalComp.props as any)?.externalTool?.toolId;
				(finalComp.props as any).externalTool.parameters.sql = ensureQueryLimit(etSql, DEFAULT_QUERY_LIMIT, MAX_COMPONENT_QUERY_LIMIT, getDbTypeFromToolId(etToolId));
			}

			finalComponents.push(finalComp);
		}

		logger.info(`[REPORT_COMP_REQ] Built ${finalComponents.length} final components`);

		// Validate externalTool SQL queries
		const validatedComponents = await validateAllExternalToolQueries(
			finalComponents, collections, tools, model, apiKey
		);

		logger.info(`[REPORT_COMP_REQ] ${validatedComponents.length}/${finalComponents.length} components validated`);

		// Strip SQL from components — only queryIds sent to frontend
		const securedComponents = validatedComponents.map(comp => {
			const props: any = { ...comp.props };

			// Handle externalTool.parameters.sql → replace with queryId
			const sqlValue = props.externalTool?.parameters?.sql || props.externalTool?.parameters?.query;
			if (sqlValue) {
				const { sql, query, ...restParams } = props.externalTool.parameters;
				const toolId = props.externalTool?.toolId || '';
				const cacheKey = toolId ? `${toolId}:${sqlValue}` : sqlValue;
				const cachedData = queryCache.get(cacheKey);
				const queryId = queryCache.storeQuery(sqlValue, cachedData);
				props.externalTool = {
					...props.externalTool,
					parameters: { queryId, ...restParams },
				};
				logger.info(`[REPORT_COMP_REQ] Replaced SQL with queryId for ${comp.name}: ${queryId}`);
			}

			// Handle legacy direct props.query → replace with queryId
			if (props.query) {
				const { query, ...restProps } = props;
				const queryId = queryCache.storeQuery(query);
				return { ...comp, props: { ...restProps, queryId } };
			}

			return { ...comp, props };
		});

		return {
			success: true,
			data: {
				components: securedComponents,
				reportTitle: parsedResult.reportTitle || 'Report',
				reportDescription: parsedResult.reportDescription || '',
				executedTools: executedTools.length > 0 ? executedTools : undefined
			},
			errors: []
		};
	} catch (error) {
		const errorMsg = error instanceof Error ? error.message : String(error);
		logger.error(`[REPORT_COMP_REQ] Error generating report: ${errorMsg}`);

		userPromptErrorLogger.logError('REPORT_COMP_REQ', error instanceof Error ? error : new Error(errorMsg), {
			prompt,
			componentsCount: components.length,
			toolsCount: tools?.length || 0
		});

		errors.push(errorMsg);
		return { success: false, errors };
	}
}

/**
 * Validate all component externalTool SQL queries with retry + LLM fix.
 */
async function validateAllExternalToolQueries(
	components: Component[],
	collections?: CollectionRegistry,
	tools?: Tool[],
	model?: string,
	apiKey?: string
): Promise<Component[]> {
	if (!collections?.['external-tools']?.['execute']) {
		logger.warn(`[REPORT_COMP_REQ] external-tools.execute not available, skipping validation`);
		return components;
	}

	const validated: Component[] = [];

	for (const comp of components) {
		const extToolId = (comp.props as any)?.externalTool?.toolId;
		const extToolName = (comp.props as any)?.externalTool?.toolName;
		const extToolParams = (comp.props as any)?.externalTool?.parameters;
		let extToolSql = extToolParams?.sql;

		if (!extToolId) {
			// No external tool (e.g., markdown components)
			validated.push(comp);
			continue;
		}

		// For non-SQL tools (REST API, GraphQL), prefetch data without SQL validation
		if (!extToolSql) {
			try {
				const result = await collections['external-tools']['execute']({
					toolId: extToolId, toolName: extToolName, ...extToolParams, data: {},
				});

				if (result?.success !== false && !result?.error) {
					const toolResult = result?.data ?? result;
					const valueKey = (comp.props as any)?.config?.valueKey;
					const isKpi = comp.type === 'KPICard' || comp.name === 'DynamicKPICard';

					// KPI cards read a single top-level aggregate (e.g. totalDeadstockValue,
					// totalBranches) that tools return alongside the rows array
					// ({ totalX, data: [...] }). Flattening to the rows array drops the
					// aggregate, so the card's data[0][valueKey] resolves to N/A. For KPI
					// cards whose valueKey is a top-level field, keep the aggregate object.
					let dataArray: any[];
					if (
						isKpi && valueKey &&
						toolResult && typeof toolResult === 'object' && !Array.isArray(toolResult) &&
						toolResult[valueKey] !== undefined
					) {
						dataArray = [toolResult];
					} else {
						const resultData = (toolResult as any)?.data ?? toolResult ?? [];
						dataArray = Array.isArray(resultData) ? resultData : [resultData];
					}

					if (!(comp.props as any).config) {
						(comp.props as any).config = {};
					}
					(comp.props as any).config.data = dataArray;

					logger.info(`[REPORT_COMP_REQ] ✓ ${comp.name} prefetched ${dataArray.length} ${dataArray.length === 1 && isKpi ? 'aggregate' : 'rows'} (non-SQL tool)`);
				}
			} catch (err) {
				logger.warn(`[REPORT_COMP_REQ] ⚠ ${comp.name} non-SQL prefetch failed: ${err instanceof Error ? err.message : String(err)}`);
			}
			validated.push(comp);
			continue;
		}

		const matchedTool = tools?.find(t => t.id === extToolId);
		let isValid = false;
		let attempts = 0;

		while (attempts < MAX_QUERY_VALIDATION_RETRIES && !isValid) {
			attempts++;
			try {
				const result = await collections['external-tools']['execute']({
					toolId: extToolId, toolName: extToolName, sql: extToolSql, data: {},
				});

				if (result?.success === false || result?.error) {
					const errorMsg = result?.error || 'Unknown error';
					throw new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
				}

				// Success — update SQL, cache result, and embed prefetched data
				(comp.props as any).externalTool.parameters.sql = extToolSql;
				const cacheKey = extToolId ? `${extToolId}:${extToolSql}` : extToolSql;
				queryCache.set(cacheKey, result?.data ?? result);

				// Extract the data array from the result
				const resultData = result?.data?.data ?? result?.data ?? [];
				const dataArray = Array.isArray(resultData) ? resultData : [resultData];

				if (!(comp.props as any).config) {
					(comp.props as any).config = {};
				}
				(comp.props as any).config.data = dataArray;

				logger.info(`[REPORT_COMP_REQ] ✓ ${comp.name} validated + cached + prefetched ${dataArray.length} rows (attempt ${attempts})`);
				validated.push(comp);
				isValid = true;
			} catch (execError) {
				const errorMsg = execError instanceof Error ? execError.message : String(execError);
				logger.warn(`[REPORT_COMP_REQ] ✗ ${comp.name} failed (attempt ${attempts}/${MAX_QUERY_VALIDATION_RETRIES}): ${errorMsg}`);

				if (attempts >= MAX_QUERY_VALIDATION_RETRIES) {
					logger.error(`[REPORT_COMP_REQ] Max retries for ${comp.name}, excluding`);
					break;
				}

				// LLM fix
				try {
					const toolSchema = matchedTool?.description || 'Schema not available';
					const databaseRules = await promptLoader.loadDatabaseRules();

					const fixPrompt = `You are a SQL expert. Fix the following SQL query that failed execution.

## Database Schema
${toolSchema}

## Database-Specific SQL Rules
${databaseRules}

## Component Context
- Component Name: ${comp.name}
- Component Type: ${comp.type}
- Title: ${(comp.props as any)?.title || 'N/A'}

## Failed Query
\`\`\`sql
${extToolSql}
\`\`\`

## Error Message
${errorMsg}

## Instructions
1. Analyze the error and fix the query
2. Ensure the fixed query follows database-specific SQL rules
3. Return ONLY the fixed SQL query

Fixed SQL query:`;

					const fixResponse = await LLM.text(
						{
							sys: 'You are a SQL expert. Return only the fixed SQL query with no additional text.',
							user: fixPrompt,
						},
						{ model: model || undefined, maxTokens: 2048, temperature: 0, apiKey }
					);

					let fixedSql = fixResponse.trim();
					fixedSql = fixedSql.replace(/^```sql\s*/i, '').replace(/\s*```$/i, '');
					fixedSql = fixedSql.replace(/^```\s*/i, '').replace(/\s*```$/i, '');

					const { query: validatedSql } = validateAndFixSqlQuery(fixedSql);

					if (validatedSql && validatedSql !== extToolSql) {
						extToolSql = ensureQueryLimit(validatedSql, DEFAULT_QUERY_LIMIT, MAX_COMPONENT_QUERY_LIMIT, getDbTypeFromToolId(extToolId));
						logger.info(`[REPORT_COMP_REQ] LLM provided SQL fix for ${comp.name}, retrying...`);
					} else {
						logger.warn(`[REPORT_COMP_REQ] LLM returned same or empty query for ${comp.name}`);
						break;
					}
				} catch (fixError) {
					const fixMsg = fixError instanceof Error ? fixError.message : String(fixError);
					logger.error(`[REPORT_COMP_REQ] Failed to get LLM SQL fix: ${fixMsg}`);
					break;
				}
			}
		}
	}

	return validated;
}
